// Copyright ©️ 2026 eVolve MEP, LLC

import { use } from 'react';

import { APSViewerStoreExtension } from 'components/Autodesk/extensions/APSViewerStoreExtension/APSViewerStoreExtension';
import { DimensioningExtension } from 'components/Autodesk/extensions/DimensioningExtension/DimensioningExtension';
import { MarkupModeExtension } from 'components/Autodesk/extensions/MarkupModeExtension/MarkupModeExtension';
import { PageNavExtension } from 'components/Autodesk/extensions/PageNavExtension';
import { SelectionToolsExtension } from 'components/Autodesk/extensions/SelectionToolsExtension/SelectionToolsExtension';
import { SpoolsExtension } from 'components/Autodesk/extensions/SpoolsExtension/SpoolsExtension';
import {
  type AutodeskExtensionStoreName,
  autodeskExtensionStores,
  type StoreFor,
} from 'components/Autodesk/useAutodeskExtensionStore';
import { isNotNil } from 'helpers/isNotNil';
import { strongEntries } from 'helpers/strongEntries';

type EvolveExtension<T extends Autodesk.Viewing.Extension> = {
  extensionClass: T;
  requiredStores: AutodeskExtensionStoreName[];
};

export const evolveExtensions = {
  APSViewerStoreExtension: {
    extensionClass: APSViewerStoreExtension,
    requiredStores: ['apsViewer'],
  },
  EvolveDimensioningExtension: {
    extensionClass: DimensioningExtension,
    requiredStores: ['apsViewer', 'spools', 'dimensioning', 'elementTags'],
  },
  MarkupModeExtension: {
    extensionClass: MarkupModeExtension,
    requiredStores: ['viewerMarkup'],
  },
  SelectionToolsExtension: {
    extensionClass: SelectionToolsExtension,
    requiredStores: [],
  },
  SpoolsExtension: {
    extensionClass: SpoolsExtension,
    requiredStores: ['spools'],
  },
  PageNavExtension: {
    extensionClass: PageNavExtension,
    requiredStores: [],
  },
  // This is a record of generics that extend `Autodesk.Viewing.Extension`.
  // oxlint-disable-next-line typescript/no-explicit-any
} as const satisfies Record<string, EvolveExtension<any>>;

export type EvolveExtensionId = keyof typeof evolveExtensions;

strongEntries(evolveExtensions).forEach(([extensionId, { extensionClass }]) => {
  Autodesk.Viewing.theExtensionManager.registerExtension(extensionId, extensionClass);
});

/**
 * Loads contexts for all evolve extensions and provides a function to determine if an extension can be loaded based on
 * the availability of its required stores.
 *
 * TODO: add a function to dynamically load the extensions.
 */
export const useEvolveExtensions = () => {
  // `use` has to be called at top-level, but can be called in loops and branching statements unlike most hooks,
  // per react-dev's docs: https://react.dev/reference/react/use
  // This has a big drawback. We can't memoize because `use` can't be inside a callback.
  // On the other hand, this allows us to dynamically load contexts for all Evolve extensions without having to
  // explicitly call a hook for each one.
  const contexts: Partial<{
    [s in AutodeskExtensionStoreName]: StoreFor<s>;
  }> = {};
  for (const [storeName, { StoreContext }] of strongEntries(autodeskExtensionStores)) {
    contexts[storeName] = use(StoreContext) as
      // oxlint-disable-next-line typescript/no-explicit-any
      StoreFor<any> | undefined;
  }

  const canLoad = (extensionId: EvolveExtensionId) => {
    const requiredStores = evolveExtensions[extensionId].requiredStores;
    return requiredStores.every((storeName) => isNotNil(contexts[storeName]));
  };

  const loadEvolveExtension = async (
    viewer: Autodesk.Viewing.GuiViewer3D,
    extensionId: EvolveExtensionId,
    opts: object,
  ) => {
    if (canLoad(extensionId)) {
      await viewer.loadExtension(extensionId, opts);
    }
  };

  return { contexts, canLoad, loadEvolveExtension };
};
